import confetti from 'canvas-confetti';

const MODAL_TRANSITION_MS = 250;

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function celebrateCompletion() {
  const duration = 750;
  const end = Date.now() + duration;
  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, zIndex: 10002, disableForReducedMotion: true });
    confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, zIndex: 10002, disableForReducedMotion: true });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

export function waitFor(conditionFn, timeoutMs = 5000, intervalMs = 50) {
  return new Promise(resolve => {
    const start = Date.now();
    const check = () => {
      let result;
      try {
        result = conditionFn();
      } catch {
        result = false;
      }
      if (result) {
        resolve(result);
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        resolve(false);
        return;
      }
      setTimeout(check, intervalMs);
    };
    check();
  });
}

export function waitForTarget(target, timeoutMs = 6000) {
  if (!target || target === 'body') return Promise.resolve(true);
  return waitFor(() => document.querySelector(target), timeoutMs);
}

export async function findFirstVisibleIndex(steps, fromIndex) {
  for (let idx = fromIndex; idx < steps.length; idx += 1) {
    const shouldSkip = steps[idx].skip ? await steps[idx].skip() : false;
    if (!shouldSkip) return idx;
  }
  return -1;
}

export function buildTourSteps({
  isSuperadmin,
  contextRef,
  setIsOrgBioModalOpen,
  setSelectedVenueId,
  setTourTargetShowId,
  setActiveTab,
  venueSectionApiRef,
  showsSectionApiRef,
  dataSectionApiRef,
}) {
  const noVenues = () => (venueSectionApiRef.current?.getActiveVenues()?.length ?? 0) === 0;
  const getFirstVenue = () => venueSectionApiRef.current?.getActiveVenues()?.[0] ?? null;
  const resolveFirstShow = async () => {
    const loaded = await waitFor(() => showsSectionApiRef.current?.hasLoaded?.() === true, 6000);
    if (!loaded) return null;
    return showsSectionApiRef.current?.getFirstShowForTour() ?? null;
  };

  const steps = [
    {
      target: 'body',
      placement: 'center',
      content: "Welcome to Showrunner! Let's take a quick tour of your dashboard.",
    },
    {
      target: '[data-tour="edit-bio-button"]',
      content: "Click the pencil to edit your organization's bio, website, Instagram, and contact info.",
      onExit: async () => {
        setIsOrgBioModalOpen(true);
        await sleep(MODAL_TRANSITION_MS);
      },
    },
    {
      target: 'body',
      placement: 'center',
      content: 'Update any of these fields, then hit Save.',
      onExit: async () => {
        setIsOrgBioModalOpen(false);
        await sleep(MODAL_TRANSITION_MS);
      },
    },
    {
      target: '[data-tour="tab-manage"]',
      content: 'This is the Manage tab — add and edit your venues and shows here.',
    },
    {
      target: '[data-tour="add-venue-button"]',
      content: 'Click here any time to add a new venue.',
    },
    {
      target: '[data-tour="venue-card-0"]',
      content: 'Select a venue to manage its shows.',
      skip: noVenues,
      onExit: async () => {
        const first = getFirstVenue();
        if (first) setSelectedVenueId(first.venue_id);
        await sleep(500);
      },
    },
    {
      target: '[data-tour="venue-edit-button-0"]',
      content: "Click the pencil to edit a venue's details, including hours.",
      skip: noVenues,
      onExit: async () => {
        const first = getFirstVenue();
        if (first) venueSectionApiRef.current?.openEditModalForVenue(first);
        await sleep(MODAL_TRANSITION_MS);
      },
    },
    {
      target: 'body',
      placement: 'center',
      content: 'Update name, address, hours, or mark it closed, then Save.',
      skip: noVenues,
      onExit: async () => {
        venueSectionApiRef.current?.closeVenueModal();
        await sleep(MODAL_TRANSITION_MS);
      },
    },
    {
      target: '[data-tour="venue-archive-button-0"]',
      content: 'Archive a venue to remove it while keeping its history.',
      skip: () => !isSuperadmin || noVenues(),
    },
    {
      target: '[data-tour="show-edit-button"]',
      content: "Click the pencil to edit a show's details.",
      skip: async () => {
        if (noVenues()) return true;
        const show = await resolveFirstShow();
        contextRef.current.firstShow = show;
        if (!show) return true;
        setTourTargetShowId(show.show_id);
        await waitForTarget('[data-tour="show-edit-button"]', 3000);
        return false;
      },
      onExit: async () => {
        const show = contextRef.current.firstShow;
        if (show) showsSectionApiRef.current?.openEditModalFor(show);
        await sleep(MODAL_TRANSITION_MS);
      },
    },
    {
      target: 'body',
      placement: 'center',
      content: 'Update dates, description, and images, then Save.',
      skip: () => !contextRef.current.firstShow,
      onExit: async () => {
        showsSectionApiRef.current?.closeShowModal();
        await sleep(MODAL_TRANSITION_MS);
      },
    },
    {
      target: '[data-tour="show-hide-button"]',
      content: 'Hide a show to pull it from the public feed without deleting it — unhide it any time.',
      skip: () => !contextRef.current.firstShow,
    },
    {
      target: '[data-tour="show-combine-button"]',
      content: 'Combine merges a duplicate show into another, keeping all its views, clicks, and seen counts.',
      skip: () => !contextRef.current.firstShow,
    },
    {
      target: '[data-tour="add-show-button"]',
      content: 'Click here to add a new show for this venue.',
      skip: noVenues,
    },
    {
      target: '[data-tour="tab-analytics"]',
      skipScroll: false,
      content: "Now let's check out your Analytics.",
      onExit: async () => {
        setActiveTab('analytics');
        await waitFor(() => dataSectionApiRef.current?.isLoading() === false, 8000);
      },
    },
    {
      target: '[data-tour="analytics-kpis"]',
      content: 'Here are your total views, clicks, and seen counts for the selected period.',
    },
    {
      target: '[data-tour="analytics-venue-filter"]',
      content: 'If you manage multiple venues, filter the chart to just one here.',
      skip: () => !dataSectionApiRef.current?.isMultiVenue(),
    },
    {
      target: '[data-tour="period-30d"]',
      content: 'Choose a time period to analyze.',
      onExit: () => dataSectionApiRef.current?.clickPeriod(30),
    },
    {
      target: '[data-tour="granularity-day"]',
      content: 'Switch between daily, weekly, or monthly breakdowns.',
      onExit: () => dataSectionApiRef.current?.clickGranularity('day'),
    },
    {
      target: '[data-tour="legend-toggle-impressions"]',
      content: 'Click a legend item to toggle that line on the chart.',
      onExit: async () => {
        dataSectionApiRef.current?.toggleImpressionsLine();
        await sleep(900);
        dataSectionApiRef.current?.toggleImpressionsLine();
      },
    },
    {
      target: 'body',
      placement: 'center',
      content: "That's it! You can replay this tour any time from the ? icon next to your organization name.",
    },
  ];

  return steps.map(step => ({ skipScroll: true, ...step }));
}